import isBoolean from 'lodash/isBoolean';
import { ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { DESKTOP_FOOTER_HEIGHT, HEADER_HEIGHT, MOBILE_FOOTER_HEIGHT } from '~src/branding/constants';
import { defaultSideMargin, PageMargin } from '~src/branding/spacing';
import { useDisplayContext } from '~src/context/DisplayContext';

import { useRootStyle } from '~src/hooks/useRootStyle';
import { Address } from '~src/model';

import { Header } from './Header';

import 'antd/dist/antd.css';

const backgroundImageStyle: CSSProperties = {
  content: '""',
  backgroundSize: 'cover',
  position: 'absolute',
  top: HEADER_HEIGHT,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: -1,
  opacity: 0.25,
  backgroundAttachment: 'fixed',
};

type Props = {
  children?: React.ReactNode;
  pagePreset?: 'column';
  pageStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
  header?: ReactNode;
  footer?: ReactNode;
  dashboard?: { address?: Address; back?: { to: string; label?: string } };
  flex?: boolean;
  bodyMargin?: PageMargin;
  headerMargin?: PageMargin;
  residenceID?: number | string;
  backgroundStyle?: CSSProperties;
};

export const Root = ({
  children,
  pageStyle,
  wrapperStyle,
  header = true,
  footer = true,
  bodyMargin,
  headerMargin = bodyMargin,
  backgroundStyle,
}: Props) => {
  const showDefaultHeader = isBoolean(header) && header;

  const { isMobile, height } = useDisplayContext();

  const { padding } = useRootStyle();

  const footerHeight = isMobile ? MOBILE_FOOTER_HEIGHT : DESKTOP_FOOTER_HEIGHT;
  const pageHeight = height - (footer ? footerHeight : 0);

  return (
    <>
      {showDefaultHeader ? <Header margin={headerMargin} /> : header}

      <PageWrapper pageHeight={pageHeight} style={wrapperStyle}>
        <StyledPage
          {...{
            bodyMargin,
            style: { ...pageStyle, minHeight: pageHeight, ...(header && { paddingTop: HEADER_HEIGHT }) },
            isMobile,
            padding,
            backgroundStyle,
          }}
        >
          {children}
        </StyledPage>
      </PageWrapper>

      {footer}
    </>
  );
};

const PageWrapper = styled.div<{
  pageHeight: number;
  style?: CSSProperties;
}>(({ pageHeight, style }) => ({
  minHeight: pageHeight,
  display: 'flex',
  flexFlow: 'column wrap',
  ...style,
}));

const StyledPage = styled.div<{
  isMobile?: boolean;
  padding?: number;
  backgroundStyle?: CSSProperties;
  bodyMargin?: PageMargin;
}>(({ bodyMargin = 'both', isMobile, padding, backgroundStyle }) => ({
  width: '100%',
  ...(backgroundStyle
    ? {
        position: 'relative',
        minHeight: '100vh',
        '&:before': {
          ...backgroundImageStyle,
          ...backgroundStyle,
        },
      }
    : { height: '100%', background: 'white' }),
  ...(isMobile ? { paddingLeft: padding, paddingRight: padding } : {}),
  ...(isMobile ? { paddingLeft: padding, paddingRight: padding } : defaultSideMargin(bodyMargin)),
  overflowX: 'hidden',
}));
